<template>
  <div class="step-title-container">
    <!-- Заголовок шага -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect
            x="4"
            y="5"
            width="16"
            height="14"
            rx="3"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="M8 9h8M8 13h6"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Основная информация
        </span>

        <h3 class="step-heading-title">
          Название вакансии
        </h3>
      </div>
    </div>

    <!-- Описание -->
    <p class="step-description">
      Укажите название позиции, на которую вы ищете сотрудника.
      Используйте понятное и узнаваемое название профессии.
    </p>

    <!-- Примеры -->
    <div class="examples">
      <span class="examples-label">
        Например:
      </span>

      <button
        v-for="example in examples"
        :key="example"
        type="button"
        class="example-chip"
        @click="selectExample(example)"
      >
        {{ example }}
      </button>
    </div>

    <!-- Поле -->
    <div class="field-container">
      <div class="field-heading">
        <label class="field-label">
          Наименование вакансии
        </label>

        <span
          class="characters-counter"
          :class="{
            warning: titleLength >= MAX_TITLE_LENGTH - 10
          }"
        >
          {{ titleLength }}/{{ MAX_TITLE_LENGTH }}
        </span>
      </div>

      <div
        class="input-shell"
        :class="{
          valid: isValid,
          error: showError
        }"
      >
        <div class="input-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M8 7V5.8A2.8 2.8 0 0 1 10.8 3h2.4A2.8 2.8 0 0 1 16 5.8V7"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <rect
              x="3"
              y="7"
              width="18"
              height="13"
              rx="3"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M3.5 12.5c5.5 2 11.5 2 17 0M10 13.8h4"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <input
          id="vacancy-title"
          v-model="title"
          type="text"
          class="title-input"
          placeholder="Например: Senior iOS разработчик"
          :maxlength="MAX_TITLE_LENGTH"
          autocomplete="off"
          spellcheck="false"
          @input="handleModelUpdate"
        />

        <Transition name="status">
          <div
            v-if="isValid"
            class="valid-status"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </Transition>
      </div>

      <!-- Сообщение -->
      <Transition name="message" mode="out-in">
        <div
          v-if="showError"
          key="error"
          class="field-message error-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M10 6.3v4.4M10 13.6v.1"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Укажите название вакансии
        </div>

        <div
          v-else
          key="hint"
          class="field-message hint-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.5"
            />

            <path
              d="M10 9v5M10 6.2v.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Хорошее название содержит профессию и уровень специалиста
        </div>
      </Transition>
    </div>

    <!-- Рекомендация -->
    <div class="recommendation">
      <div class="recommendation-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 18h6M10 21h4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M8.5 15.5c-1.5-1.1-2.5-2.9-2.5-5a6 6 0 1 1 12 0c0 2.1-1 3.9-2.5 5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <p>
        Избегайте общих названий вроде «Сотрудник» или «Специалист».
        Конкретное название помогает получить больше релевантных откликов.
      </p>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch
} from "vue";

const emit = defineEmits([
  "complete-step",
  "valid",
  "notValid"
]);

const MAX_TITLE_LENGTH = 80;

const examples = [
  "UI-дизайнер",
  "Маркетолог",
  "iOS разработчик"
];

const title = ref(
  localStorage.getItem("vacancy_title") || ""
);

const isTouched = ref(false);

const normalizedTitle = computed(() =>
  title.value.trim()
);

const titleLength = computed(() =>
  title.value.length
);

/*
  Сохраняем прежнюю бизнес-логику:
  любое непустое название считается валидным.
*/
const isValid = computed(() =>
  normalizedTitle.value.length > 0
);

const showError = computed(() =>
  isTouched.value && !isValid.value
);

function handleModelUpdate() {
  isTouched.value = true;
}

function selectExample(example) {
  title.value = example;
  isTouched.value = true;
}

function saveTitleAndContinue() {
  isTouched.value = true;

  if (isValid.value) {
    /*
      Перед переходом сохраняем значение
      без пробелов по краям.
    */
    title.value = normalizedTitle.value;

    localStorage.setItem(
      "vacancy_title",
      title.value
    );

    emit("complete-step");
  } else {
    emit("notValid");
  }
}

watch(
  title,
  newValue => {
    localStorage.setItem(
      "vacancy_title",
      newValue
    );

    if (newValue.trim().length > 0) {
      emit("valid");
    } else {
      emit("notValid");
    }
  },
  {
    immediate: true
  }
);

onMounted(() => {
  document.addEventListener(
    "save-title",
    saveTitleAndContinue
  );
});

onUnmounted(() => {
  document.removeEventListener(
    "save-title",
    saveTitleAndContinue
  );
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-title-container {
  width: 100%;
  max-width: 720px;

  display: flex;
  flex-direction: column;

  margin-left: 40px;
  padding: 26px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 24px;

  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.02),
    0 12px 34px rgba(15, 23, 42, 0.04);

  animation: card-appearance 0.4s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 17px;
}

.step-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-heading-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.step-description {
  max-width: 650px;
  margin: 0 0 18px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

/* Примеры */

.examples {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;

  margin-bottom: 22px;
}

.examples-label {
  margin-right: 2px;

  color: #9299a5;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.example-chip {
  padding: 6px 9px;

  cursor: pointer;

  color: #4e74a8;
  background: rgba(55, 132, 237, 0.065);

  border: 1px solid rgba(55, 132, 237, 0.1);
  border-radius: 9px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.example-chip:hover {
  color: #ffffff;
  background: #3988ef;
  border-color: #3988ef;

  transform: translateY(-1px);
}

/* Поле */

.field-container {
  width: 100%;
  max-width: 570px;
}

.field-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.field-label {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.characters-counter {
  color: #9aa1ad;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;

  transition: color 0.2s ease;
}

.characters-counter.warning {
  color: #d99035;
}

.input-shell {
  position: relative;

  width: 100%;

  display: flex;
  align-items: center;

  padding-left: 15px;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.025);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.input-shell:hover {
  background: #fafbfc;
  border-color: #d4d9e1;
}

.input-shell:focus-within {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 24px rgba(31, 96, 205, 0.08);

  transform: translateY(-1px);
}

.input-shell.valid:not(:focus-within) {
  border-color: rgba(39, 162, 104, 0.38);
}

.input-shell.error {
  background: rgba(255, 246, 246, 0.9);
  border-color: #e15b5b;

  box-shadow:
    0 0 0 4px rgba(225, 91, 91, 0.08);
}

.input-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #949daa;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.input-icon svg {
  width: 20px;
  height: 20px;
}

.input-shell:focus-within .input-icon {
  color: #4388f5;
  transform: scale(1.05);
}

.title-input {
  min-width: 0;
  width: 100%;
}

.valid-status {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 13px;

  color: #28a46b;
  background: rgba(40, 164, 107, 0.1);

  border-radius: 50%;
}

.valid-status svg {
  width: 16px;
  height: 16px;
}

/* Сообщения */

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin-top: 8px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.45;
}

.field-message svg {
  width: 14px;
  height: 14px;

  flex-shrink: 0;
  margin-top: 1px;
}

.hint-message {
  color: #8a93a2;
}

.error-message {
  color: #d84e4e;
  font-weight: 600;
}

/* Рекомендация */

.recommendation {
  max-width: 570px;

  display: flex;
  align-items: flex-start;
  gap: 10px;

  margin-top: 21px;
  padding: 12px 13px;

  color: #667084;
  background: rgba(53, 132, 247, 0.055);

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 13px;
}

.recommendation-icon {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  color: #4388f5;
}

.recommendation-icon svg {
  width: 18px;
  height: 18px;
}

.recommendation p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.55;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.65);
}

.message-enter-from {
  opacity: 0;
  transform: translateY(-3px);
}

.message-leave-to {
  opacity: 0;
  transform: translateY(3px);
}

/* Dark theme */

html[data-theme="dark"] .step-title-container {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .step-heading-title {
  color: #f1f2f4;
}

html[data-theme="dark"] .step-label {
  color: #777f8d;
}

html[data-theme="dark"] .step-description {
  color: #a5a9b2;
}

html[data-theme="dark"] .examples-label {
  color: #777e8a;
}

html[data-theme="dark"] .example-chip {
  color: #81a9df;
  background: rgba(67, 136, 245, 0.08);
  border-color: rgba(67, 136, 245, 0.11);
}

html[data-theme="dark"] .example-chip:hover {
  color: #ffffff;
  background: #3d7fd7;
  border-color: #3d7fd7;
}

html[data-theme="dark"] .field-label {
  color: #d8dae0;
}

html[data-theme="dark"] .input-shell {
  background: #28282c;
  border-color: #3b3b41;
}

html[data-theme="dark"] .input-shell:hover {
  background: #2b2b30;
  border-color: #484850;
}

html[data-theme="dark"] .input-shell:focus-within {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .input-shell.error {
  background: rgba(91, 37, 40, 0.2);
  border-color: #d85b60;
}

html[data-theme="dark"] .input-icon {
  color: #747985;
}

html[data-theme="dark"] .input-shell:focus-within .input-icon {
  color: #669cf5;
}

html[data-theme="dark"] .hint-message {
  color: #777d88;
}

html[data-theme="dark"] .recommendation {
  color: #9ba2ae;
  background: rgba(63, 137, 241, 0.07);
  border-color: rgba(74, 145, 246, 0.12);
}

/* Mobile */

@media (max-width: 430px) {
  .step-title-container {
    width: 100%;
    max-width: none;

    margin-left: 0;
    padding: 20px 16px;

    border-radius: 18px;
  }

  .step-icon {
    width: 39px;
    height: 39px;

    border-radius: 12px;
  }

  .step-heading-title {
    font-size: 16px;
  }

  .step-description {
    font-size: 12px;
    line-height: 1.55;
  }

  .examples {
    margin-bottom: 19px;
  }

  .field-container {
    max-width: none;
  }

  .recommendation {
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-title-container {
    animation: none;
  }

  .input-shell,
  .input-icon,
  .example-chip,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}

.title-input {
  min-width: 0;
  width: 100%;
  height: 52px;

  padding: 0 12px;

  color: #252b35;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.4;
}

.title-input::placeholder {
  color: #a5acb7;
  opacity: 1;
}

.title-input:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

html[data-theme="dark"] .title-input {
  color: #f2f2f4;
  caret-color: #67a2ff;
}

html[data-theme="dark"] .title-input::placeholder {
  color: #70747c;
}

html[data-theme="dark"] .characters-counter {
  color: #7f838b;
}

html[data-theme="dark"] .characters-counter.warning {
  color: #e4a552;
}

html[data-theme="dark"] .valid-status {
  color: #51c989;
  background-color: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .error-message {
  color: #ff8b94;
}

html[data-theme="dark"] .recommendation-icon {
  color: #67a2ff;
}

</style>